import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import QuestTypeCheckout, {
  statusBadgeStyle,
} from "../widgets/QuestTypeCheckout";
import {
  questTypeMarketplace,
  addToCart,
  removeFromCart,
} from "../_slice/questTypePackageSlice";
import AppContext from "../context/AppContext";
import { HeadingContext } from "../context/HeadingContext";
import {
  daysUntil,
  formatApiDate,
  formatFriendlyDate,
  formatPrice,
  getFreeAccessUntil,
  isFreeAccessGrant,
  isRenewable,
  RENEWAL_WINDOW_DAYS,
} from "../../utils/utils";

// Purchasing is switched off on this screen for now: it is a plain listing of
// every quest type. Set this to true to bring back the bundle tabs, the cart
// and the Add to Cart / Renew actions — the code for all of it is still here.
const SHOW_PURCHASE_FLOW = false;

// Ownership state of a Quest Type inside a bundle
const getQuestTypeState = (questType, bundle) => {
  // Organisation Free Access first: it is the reason this is owned right now.
  if (isFreeAccessGrant(questType)) return "free_access";
  if (bundle?.isDefault) return "included";
  if (questType?.is_expired) return "expired";
  if (questType?.owned) return "owned";
  return "available";
};

function QuestTypePackage() {
  const [activeBundleId, setActiveBundleId] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);
  const { setHeading } = useContext(HeadingContext);

  // Redux selector for the marketplace and the cart
  const {
    marketplaceBundles,
    marketplaceStatus,
    organisationHasFreeAccess,
    cart,
    checkoutStatus,
    confirmStatus,
  } = useSelector((state) => state.questTypePackage);

  // End of the organisation's free access, from the API response itself.
  const freeAccessUntil = useMemo(
    () => (organisationHasFreeAccess ? getFreeAccessUntil(marketplaceBundles) : null),
    [organisationHasFreeAccess, marketplaceBundles]
  );

  useEffect(() => {
    setHeading("Quest Types");
  }, [setHeading]);

  // Fetch the marketplace on mount and whenever a purchase invalidates it
  useEffect(() => {
    if (marketplaceStatus === "idle") {
      dispatch(questTypeMarketplace({ navigate, setLoader }));
    }
  }, [marketplaceStatus, dispatch, navigate, setLoader]);

  // Keep a valid bundle tab selected as the API data arrives/changes
  useEffect(() => {
    if (!marketplaceBundles?.length) {
      setActiveBundleId(null);
      return;
    }
    setActiveBundleId((current) =>
      marketplaceBundles.some((bundle) => bundle.id === current)
        ? current
        : marketplaceBundles[0].id
    );
  }, [marketplaceBundles]);

  const activeBundle = useMemo(
    () =>
      marketplaceBundles?.find((bundle) => bundle.id === activeBundleId) || null,
    [marketplaceBundles, activeBundleId]
  );

  // Rows to render: the selected bundle when tabs are on, otherwise every
  // quest type in one flat list.
  const rows = useMemo(() => {
    if (SHOW_PURCHASE_FLOW) {
      return (activeBundle?.quest_types || []).map((questType) => ({
        questType,
        bundle: activeBundle,
      }));
    }
    return (marketplaceBundles || []).flatMap((bundle) =>
      (bundle.quest_types || []).map((questType) => ({ questType, bundle }))
    );
  }, [marketplaceBundles, activeBundle]);

  const isInCart = (id) => cart.some((item) => item.id === id);

  const isBusy =
    checkoutStatus === "loading" || confirmStatus === "loading";

  // Add a purchasable Quest Type to the cart
  const handleAddToCart = (questType, bundle) => {
    if (bundle?.isDefault) return;
    dispatch(
      addToCart({
        id: questType.id,
        name: questType.name,
        price: questType.price,
        image: questType.image,
        bundleId: bundle?.id,
        bundleName: bundle?.name,
      })
    );
  };

  // Render the action cell for one Quest Type row
  const renderAction = (questType, bundle) => {
    const state = getQuestTypeState(questType, bundle);

    if (state === "included") {
      return <span style={statusBadgeStyle("included")}>Included</span>;
    }

    // Granted by free access: nothing to buy while it lasts.
    if (state === "free_access") {
      return <span style={statusBadgeStyle("included")}>Free access</span>;
    }

    if (isInCart(questType.id)) {
      return (
        <div className="d-flex align-items-center justify-content-center">
          <button type="button" className="submit_btn" disabled style={{ opacity: 0.6 }}>
            In Cart
          </button>
          <button
            type="button"
            className="action_btns ml-3"
            onClick={() => dispatch(removeFromCart(questType.id))}
            disabled={isBusy}
          >
            Remove
          </button>
        </div>
      );
    }

    // Owned and not near expiry: nothing to do, so say so rather than
    // offering a purchase that only matters close to the renewal date.
    if (state === "owned" && !isRenewable(questType.expires_at)) {
      return (
        <button
          type="button"
          className="submit_btn"
          style={{ whiteSpace: "nowrap", minWidth: "120px", opacity: 0.6 }}
          disabled
        >
          Purchased
        </button>
      );
    }

    return (
      <button
        type="button"
        className="submit_btn"
        style={{ whiteSpace: "nowrap", minWidth: "120px" }}
        onClick={() => handleAddToCart(questType, bundle)}
        disabled={isBusy}
      >
        {state === "available" ? "Add to Cart" : "Renew"}
      </button>
    );
  };

  // Render the ownership/expiry cell for one Quest Type row
  const renderStatus = (questType, bundle) => {
    const state = getQuestTypeState(questType, bundle);

    if (state === "free_access") {
      return (
        <>
          <span style={statusBadgeStyle("included")}>Free access</span>
          {questType.expires_at && (
            <p className="mt-1" style={{ fontSize: "12px" }}>
              Included until {formatApiDate(questType.expires_at)}
            </p>
          )}
        </>
      );
    }

    if (state === "included") {
      return (
        <>
          <span style={statusBadgeStyle("included")}>Included</span>
          <p className="mt-1" style={{ fontSize: "12px" }}>Never expires</p>
        </>
      );
    }

    if (state === "expired") {
      return (
        <>
          <span style={statusBadgeStyle("expired")}>Expired</span>
          {questType.expires_at && (
            <p className="mt-1" style={{ fontSize: "12px" }}>
              Expired on {formatFriendlyDate(questType.expires_at)}
            </p>
          )}
        </>
      );
    }

    if (state === "owned") {
      const remaining = daysUntil(questType.expires_at);
      const expiringSoon =
        remaining !== null && remaining <= RENEWAL_WINDOW_DAYS;
      return (
        <>
          <span style={statusBadgeStyle(expiringSoon ? "expiring" : "owned")}>
            {expiringSoon ? "Expiring soon" : "Owned"}
          </span>
          {questType.expires_at && (
            <p className="mt-1" style={{ fontSize: "12px" }}>
              Expires: {formatFriendlyDate(questType.expires_at)}
            </p>
          )}
        </>
      );
    }

    return <span style={statusBadgeStyle("available")}>Not purchased</span>;
  };

  return (
    <>
      <div className="org">
        <div className="org_header d-flex align-items-center justify-content-between">
          <h1 className="org_header_heading">Quest Types</h1>
          {SHOW_PURCHASE_FLOW && <QuestTypeCheckout />}
        </div>

        {/* Bundle tabs — names come straight from the marketplace API */}
        {SHOW_PURCHASE_FLOW && marketplaceBundles?.length > 0 && (
          <ul className="bundle_tabs">
            {marketplaceBundles.map((bundle) => (
              <li
                key={`bundle_${bundle.id}`}
                className={`bundle_tabs_item ${
                  bundle.id === activeBundleId ? "activeTab" : ""
                }`}
                onClick={() => setActiveBundleId(bundle.id)}
              >
                {bundle.name}
              </li>
            ))}
          </ul>
        )}

        {freeAccessUntil && (
          <p className="bundle_note">
            Your organisation's free access ends on{" "}
            {formatApiDate(freeAccessUntil)}.
          </p>
        )}

        {SHOW_PURCHASE_FLOW && activeBundle?.isDefault && (
          <p className="bundle_note">
            Quest types in this bundle are included with your account. They are
            free, always available and never expire.
          </p>
        )}

        <div className="org_table table-responsive table-border-0">
          <table className="table">
            <thead>
              <tr>
                <td className="org_table_column w-10 zone_table_heading"> </td>
                <td className="org_table_column w-30 zone_table_heading">
                  Quest Type
                </td>
                <td className="org_table_column w-15 zone_table_heading">
                  Price
                </td>
                <td className="org_table_column w-20 zone_table_heading">
                  Status
                </td>
                {SHOW_PURCHASE_FLOW && (
                  <td className="org_table_column w-25 text-center zone_table_heading">
                    Action
                  </td>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map(({ questType, bundle }) => (
                  <tr key={`questType_${questType.id}`}>
                    <td className="org_table_row w-10 zone_body_text">
                      {questType.image && (
                        <img
                          src={`${process.env.REACT_APP_MEDIA_URL_ORG}${questType.image}`}
                          alt={questType.name || ""}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </td>
                    <td className="org_table_row w-30 zone_body_text">
                      {questType.name || "-"}
                    </td>
                    <td className="org_table_row w-15 zone_body_text">
                      {/* Per Quest Type price — the bundle has no price any more */}
                      {isFreeAccessGrant(questType) ? (
                        <>
                          Free
                          <p className="mt-1" style={{ fontSize: "12px" }}>
                            Normally {formatPrice(questType.price)}
                          </p>
                        </>
                      ) : bundle?.isDefault ? (
                        "Free"
                      ) : (
                        formatPrice(questType.price)
                      )}
                    </td>
                    <td className="org_table_row w-20 zone_body_text">
                      {renderStatus(questType, bundle)}
                    </td>
                    {SHOW_PURCHASE_FLOW && (
                      <td className="org_table_row w-25 zone_body_text">
                        <div className="d-flex align-items-center justify-content-center">
                          {renderAction(questType, bundle)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    className="org_table_row zone_body_text"
                    colSpan={SHOW_PURCHASE_FLOW ? 5 : 4}
                  >
                    {marketplaceStatus === "loading"
                      ? "Loading quest types..."
                      : "No quest types available."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
}

export default QuestTypePackage;
